import React, { useEffect, useRef, useState } from 'react';
import CommonButton from '../../Components/CommonButton';
import { useNova } from '../NovaContext';
import ErrorRow from '../components/ErrorRow';
import Glyph from '../components/Glyph';
import Message, { TypingIndicator } from '../components/Message';
import TabPanel from '../components/TabPanel';
import { T, Title } from '../components/Text';
import { QUICK_ACTIONS, SUGGESTED } from '../constants';
import { IconArrowDown, IconChevronRight, IconPlus } from '../icons';
import { dayLabel, greetingFor } from '../utils';

function Welcome() {
    const { actions } = useNova();

    return (
        <div className="nova_welcome">
            <section className="nova_greeting">
                <Title size="3xl" className="nova_greeting_title">{greetingFor()}</Title>
                <T as="p" size="md" className="nova_intro">
                    I'm Foresee AI, your security assistant. Ask about your security posture, open risks or recent platform activity.
                </T>
            </section>

            <section className="nova_quick_grid" aria-label="Quick actions">
                {QUICK_ACTIONS.map(({ title, description, glyph, prompt }) => (
                    <button
                        key={title}
                        type="button"
                        className="nova_tile nova_tile--quick cmn_elevated_card"
                        onClick={() => (prompt ? actions.send(prompt) : actions.focusInput())}
                    >
                        <span className="nova_icon_box"><Glyph name={glyph} size={20} /></span>
                        <T size="md" weight="semibold" className="nova_text_strong">{title}</T>
                        <T size="base" className="nova_muted">{description}</T>
                    </button>
                ))}
            </section>

            <section>
                <T as="h3" size="sm" weight="semibold" className="nova_label">Suggested</T>
                <ul className="nova_suggest_list">
                    {SUGGESTED.map((question) => (
                        <li key={question}>
                            <button type="button" className="nova_tile nova_tile--row cmn_elevated_card" onClick={() => actions.send(question)}>
                                <T size="md" className="nova_text_strong">{question}</T>
                                <IconChevronRight size={16} />
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}

function Conversation() {
    const { state, actions } = useNova();
    const { messages, sending, error } = state;

    return (
        <div className="nova_conversation">
            <div className="nova_chat_bar">
                <CommonButton
                    type="button"
                    variant="white"
                    className="nova_btn nova_btn--sm"
                    onClick={actions.newChat}
                    text={<><IconPlus size={14} />New chat</>}
                />
            </div>
            {messages.length > 0 && (
                <div className="nova_sep">
                    <span className="nova_sep_line" />
                    <T size="sm" weight="medium" className="nova_muted">{dayLabel(messages[0].createdAt)}</T>
                    <span className="nova_sep_line" />
                </div>
            )}
            {messages.map((message, index) => (
                <Message key={message.id} message={message} isLast={index === messages.length - 1} />
            ))}
            {sending && <TypingIndicator />}
            {error && <ErrorRow message={error.message} onRetry={error.text ? actions.retry : undefined} />}
        </div>
    );
}

export default function ChatPage() {
    const { state } = useNova();
    const { messages, sending, error } = state;
    const scrollRef = useRef(null);
    const stickToBottom = useRef(true);
    const [showJump, setShowJump] = useState(false);
    const isWelcome = messages.length === 0 && !sending && !error;

    useEffect(() => {
        if (!isWelcome) return;
        stickToBottom.current = true;
        setShowJump(false);
    }, [isWelcome]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || isWelcome) return;
        if (stickToBottom.current) el.scrollTop = el.scrollHeight;
        else setShowJump(true);
    }, [messages.length, sending, error, isWelcome]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (isWelcome) el.scrollTop = 0;
        else if (stickToBottom.current) el.scrollTop = el.scrollHeight;
    }, [state.open, state.activeTab, isWelcome]);

    const onScroll = () => {
        const el = scrollRef.current;
        stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        if (stickToBottom.current) setShowJump(false);
    };

    const jumpToLatest = () => {
        const el = scrollRef.current;
        el.scrollTop = el.scrollHeight;
        stickToBottom.current = true;
        setShowJump(false);
    };

    const jump = showJump && !isWelcome && (
        <div className="nova_jump">
            <CommonButton
                type="button"
                className="nova_btn nova_btn--sm nova_btn--pill"
                onClick={jumpToLatest}
                text={<><IconArrowDown size={14} />Jump to latest</>}
            />
        </div>
    );

    return (
        <TabPanel id="chat" scrollRef={scrollRef} onScroll={onScroll} overlay={jump || null}>
            {isWelcome ? <Welcome /> : <Conversation />}
        </TabPanel>
    );
}
